import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'

/**
 * Agentic AI e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve altı madde İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. "Agentic AI" çevrilmiyor — e-kitabın adı ve
 * kapağında basılı olan lettering o; "agent", "chatbot", "governance" gibi
 * yerleşik terimler de İngilizce kalıyor.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */
export const ebookAgenticAi: WrittenPage = {
  navLabel: 'Agentic AI',
  title: 'Agentic AI: A Practical Guide',
  description:
    'Şirketlerin nasıl düşündüğünü, karar verdiğini ve harekete geçtiğini otonom AI agent’larıyla yeniden tanımlıyoruz. Digitopia Impact Labs’in Agentic AI rehberini indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Pratik bir rehber',
      headline: 'Agentic AI',
      sub: 'Şirketlerin nasıl düşündüğünü, karar verdiğini ve harekete geçtiğini otonom AI agent’larıyla yeniden tanımlıyoruz.',
      body: [
        'Otonom AI agent’larının gerçek zamanlı planlayarak, öğrenerek ve harekete geçerek işi '
          + 'nasıl yeniden şekillendirdiğini keşfedin. Önde giden kurumların agentic AI ile nasıl '
          + 'hız, verimlilik ve stratejik üstünlük kazandığını görün.',
      ],
      ctas: [{ label: 'İndirin', href: LINKS.agenticAiEbookPdf }],
      photo: {
        src: '/photo/agentic-ai-ebook-artwork.webp',
        alt: 'E-kitabın kapak görseli: siyah zemin üzerinde mavi ve mor ışık noktalarından oluşan bir küre.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Neden indirmelisiniz?',
      sub:
        'Agentic AI, karmaşıklığı azaltan, tekrar eden işi ortadan kaldıran ve karar almayı '
        + 'hızlandıran “dijital iş arkadaşları” çağını açıyor. Yeni teknolojileri pilot olarak '
        + 'deniyor da olun, şirket çapında bir dönüşüm planlıyor da olun; bu e-kitap '
        + 'stratejinizi reaktif AI’ın ötesine, akıllı otonomiye taşıyacak içgörüleri veriyor.',
      ctas: [{ label: 'E-kitabı indirin', href: LINKS.agenticAiEbookPdf }],
      photo: {
        src: '/photo/agentic-ai-ebook-covers.webp',
        alt: 'Agentic AI rehberinin iki kopyası, biri diğerinin arkasında, açık renkli bir zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neler öğreneceksiniz?',
      sub:
        'Agentic AI’ın chatbot’ların ve basit otomasyonun çok ötesine geçen gerçek dünya '
        + 'potansiyelini keşfedin. Rehberin içinde:',
      points: [
        'Agentic AI’ın temel kavramları ve karakteristikleri',
        'Stratejik faydalar ve iş değeri',
        'Operasyon, müşteri hizmetleri, R&D ve IT tarafından kullanım senaryoları',
        'Riskler, governance ve etik çerçeveler',
        'Agentic sistemleri hayata geçirmek ve ölçeklemek için pratik adımlar',
        'Dijital iş arkadaşı olarak AI agent’ları dahil, geleceğin trendleri',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: LINKS.agenticAiEbookPdf }],
      photo: {
        src: '/photo/agentic-ai-ebook-stack.webp',
        alt: 'Yukarıdan görünen, üst üste dizilmiş Agentic AI rehberi kopyaları.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'E-kitabı indirin',
      sub: 'E-kitabı alın ve agentic AI stratejinizi bugün kurmaya başlayın.',
      cta: { label: 'E-kitabı indirin', href: LINKS.agenticAiEbookPdf },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'Intel’in AI Readiness Assessment’ı, Digitopia’nın anlaşılır ve self-service '
            + 'platformu üzerine kurulu; müşterilerimiz ve potansiyel müşterilerimiz AI '
            + 'olgunluklarını kendi başlarına değerlendirebiliyor. Ortaya çıkan net ve eyleme '
            + 'dönük rapor, kurumsal açıkları görmeye ve kapatmaya yardımcı oluyor; aynı zamanda '
            + 'Intel’e stratejik bir AI danışmanı olarak duyulan güveni besliyor. '
            + 'Değerlendirme, sohbeti başlatıyor, müşteriye uygulanabilir içgörüler veriyor ve '
            + 'devam eden bir ilişki için değerli fırsatlar yaratıyor.',
          source: 'Victoria Kouyoumjian, Marketing Director, Intel AI',
          company: 'Intel AI',
          logo: { name: 'Intel AI', src: '/logo/intelaicard.png' },
          portrait: {
            src: '/photo/portrait-victoria-kouyoumjian.webp',
            alt: 'Victoria Kouyoumjian',
          },
        },
      ],
    },
  ],
}
